import { useQueryClient } from "@tanstack/react-query";
import {
  Activity, Boxes, Building2, ChevronsUpDown, CircleHelp, FileText, Gauge, HardDrive, LayoutDashboard, LogOut, Menu, Monitor, Moon, PlusCircle,
  Settings, ShieldCheck, Sun, SunMoon,
} from "lucide-react";
import { DropdownMenu } from "radix-ui";
import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router";
import { useApi } from "../api/client";
import { useOrgs, useOverview, useSession } from "../api/queries";
import { articleForPath } from "../help";
import { helpMessages } from "../help/help.i18n";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { initials } from "../lib/format";
import { appMessages } from "./app.i18n";
import { Brand } from "./Brand";
import { TrialBanner } from "./TrialBanner";
import { LocaleMenu } from "./LocaleMenu";
import { API_MODE } from "./mode";
import { applyTheme, readTheme, type ThemeChoice } from "./theme";

export function Shell() {
  const { orgId = "" } = useParams();
  const session = useSession();
  const orgs = useOrgs();
  const overview = useOverview(orgId);
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpenAt, setNavOpenAt] = useState<string | null>(null);
  const [theme, setTheme] = useState<ThemeChoice>(readTheme);
  const t = useT(appMessages);
  const tc = useT(common);
  const th = useT(helpMessages);

  const navOpen = navOpenAt === location.pathname;
  const setNavOpen = (open: boolean) => setNavOpenAt(open ? location.pathname : null);

  const org = orgs.data?.find((o) => o.id === orgId);
  const msp = orgs.data?.find((o) => o.kind === "msp");
  const attention = overview.data ? overview.data.counts.failed + overview.data.counts.stale + overview.data.counts.never : 0;
  const base = `/o/${orgId}`;

  function chooseTheme(t: ThemeChoice) {
    setTheme(t);
    applyTheme(t);
  }

  async function logout() {
    await api.logout();
    qc.clear();
    navigate("/login", { replace: true });
  }

  const section = location.pathname.split("/")[3] ?? "";
  const sectionName: Record<string, string> = t.section;

  return (
    <div className="shell" data-nav-open={navOpen}>
      {navOpen && <button className="scrim" aria-label={t.closeMenu} onClick={() => setNavOpen(false)} />}
      <aside className="rail" aria-label={t.mainNav}>
        <Brand />

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="org-switch" type="button">
              <div>
                <span className="org-kind">{org?.kind === "msp" ? "MSP" : t.customer}</span>
                <span className="org-name">{org?.name ?? t.loadingOrg}</span>
              </div>
              <ChevronsUpDown size={15} aria-hidden="true" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="menu" sideOffset={6} align="start">
              <DropdownMenu.Label className="menu-label">{t.switchOrg}</DropdownMenu.Label>
              {msp && (
                <DropdownMenu.Item className="menu-item" onSelect={() => navigate(`/o/${msp.id}/overview`)}>
                  <Building2 size={15} aria-hidden="true" /> {msp.name}
                  <span className="meta">{msp.machineCount}</span>
                </DropdownMenu.Item>
              )}
              {orgs.data?.filter((o) => o.kind === "customer").map((o) => (
                <DropdownMenu.Item key={o.id} className="menu-item child" onSelect={() => navigate(`/o/${o.id}/overview`)}>
                  <span className={`dot ${o.status === "ok" ? "on" : "off"}`} style={o.status === "critical" ? { background: "var(--crit-mark)" } : o.status === "attention" ? { background: "var(--warn-mark)" } : undefined} aria-hidden="true" />
                  {o.name}
                  <span className="meta">{o.machineCount}</span>
                </DropdownMenu.Item>
              ))}
              <DropdownMenu.Separator className="menu-sep" />
              {msp && (
                <DropdownMenu.Item className="menu-item" onSelect={() => navigate(`/o/${msp.id}/organizations`)}>
                  {t.allOrgs}
                </DropdownMenu.Item>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <nav className="nav">
          <NavLink to={`${base}/overview`}><LayoutDashboard aria-hidden="true" />{t.section.overview}</NavLink>
          <NavLink to={`${base}/machines`}>
            <Monitor aria-hidden="true" />{t.section.machines}
            {attention > 0 && <span className="count" aria-label={t.attention(attention)}>{attention}</span>}
          </NavLink>
          <NavLink to={`${base}/add-machines`}><PlusCircle aria-hidden="true" />{t.section["add-machines"]}</NavLink>
          <div className="nav-label">{t.groupProtection}</div>
          <NavLink to={`${base}/policies`}><ShieldCheck aria-hidden="true" />{t.section.policies}</NavLink>
          <NavLink to={`${base}/storage`}><HardDrive aria-hidden="true" />{t.section.storage}</NavLink>
          <NavLink to={`${base}/activity`}><Activity aria-hidden="true" />{t.section.activity}</NavLink>
          <NavLink to={`${base}/reports`}><FileText aria-hidden="true" />{t.section.reports}</NavLink>
          <div className="nav-label">{t.groupAccount}</div>
          <NavLink to={`${base}/usage`}><Gauge aria-hidden="true" />{t.section.usage}</NavLink>
          {/* MSP enxerga as filhas; o administrador da plataforma enxerga todas
              e é quem cadastra, esteja em que organização estiver. */}
          {(org?.kind === "msp" || session.data?.user.platformAdmin) && (
            <NavLink to={`${base}/organizations`}><Building2 aria-hidden="true" />{t.section.organizations}</NavLink>
          )}
          <NavLink to={`${base}/settings`}><Settings aria-hidden="true" />{t.section.settings}</NavLink>
          <NavLink to={`${base}/help`}><CircleHelp aria-hidden="true" />{t.section.help}</NavLink>
          {/* Armazenamento da plataforma só existe para o administrador da plataforma (ADR 0021). */}
          {session.data?.user.platformAdmin && (
            <>
              <div className="nav-label">{t.groupPlatform}</div>
              <NavLink to={`${base}/platform-storage`}><Boxes aria-hidden="true" />{t.section["platform-storage"]}</NavLink>
            </>
          )}
        </nav>

        <div className="rail-foot">
          <LocaleMenu variant="rail" />
          <div className="theme-toggle" role="group" aria-label={t.theme}>
            <button type="button" aria-pressed={theme === "light"} onClick={() => chooseTheme("light")} title={t.themeLight}><Sun aria-hidden="true" /><span className="sr-only">{t.themeLight}</span></button>
            <button type="button" aria-pressed={theme === "system"} onClick={() => chooseTheme("system")} title={t.themeSystem}><SunMoon aria-hidden="true" /><span className="sr-only">{t.themeSystemShort}</span></button>
            <button type="button" aria-pressed={theme === "dark"} onClick={() => chooseTheme("dark")} title={t.themeDark}><Moon aria-hidden="true" /><span className="sr-only">{t.themeDark}</span></button>
          </div>
          <div className="rail-user">
            <span className="avatar" aria-hidden="true">{initials(session.data?.user.name ?? "")}</span>
            <div className="who">
              <strong>{session.data?.user.name}</strong>
              <span>{session.data ? tc.role[session.data.user.role] : ""}</span>
            </div>
            <button type="button" className="btn btn-ghost btn-icon" onClick={logout} aria-label={t.signOut} title={t.signOut} style={{ marginLeft: "auto", color: "var(--rail-ink)" }}>
              <LogOut aria-hidden="true" />
            </button>
          </div>
        </div>
      </aside>

      <div className="main">
        <div className="topbar">
          <button type="button" className="btn btn-ghost btn-icon menu-btn" onClick={() => setNavOpen(true)} aria-label={t.openMenu}>
            <Menu aria-hidden="true" />
          </button>
          <div className="crumbs">
            {org?.parentId && msp && <span className="crumb-parent"><NavLink to={`/o/${msp.id}/organizations`}>{msp.name}</NavLink> <span aria-hidden="true">/</span></span>}
            <span className="crumb-parent">{org?.name} <span aria-hidden="true">/</span></span>
            <strong>{sectionName[section] ?? ""}</strong>
          </div>
          {API_MODE === "mock" && <span className="mock-flag" title={t.mockFlagTitle}>{t.mockFlag}</span>}
          {/* Ajuda contextual: o artigo da tela atual (ADR 0027). */}
          <NavLink
            className="btn btn-ghost btn-icon help-btn"
            to={`${base}/help/${articleForPath(location.pathname)}`}
            aria-label={th.helpFor(sectionName[section] ?? th.title)}
            title={th.helpFor(sectionName[section] ?? th.title)}
          >
            <CircleHelp aria-hidden="true" />
          </NavLink>
        </div>
        <main className="content" id="conteudo">
          <TrialBanner org={org} now={orgs.dataUpdatedAt || 0} />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
